import React from 'react';

import { AssetInfo } from '@/cip/_openapi/portfolio-optimizer';
import { formatPercent } from 'utils';

export interface ModelStrategyHolding {
    securityId: string;
    weightPercent: number;
    replacementSecurityIds: string[];
}

interface HoldingSource {
    securityId: string;
    weight: string;
    replacementAssets?: Array<{ securityId: string }>;
}

const toHoldings = (assetAllocations: HoldingSource[], toPercent: (weight: string) => number): ModelStrategyHolding[] =>
    assetAllocations
        .map((asset) => ({
            securityId: asset.securityId,
            weightPercent: toPercent(asset.weight),
            replacementSecurityIds: (asset.replacementAssets ?? []).map((replacement) => replacement.securityId),
        }))
        .sort((a, b) => b.weightPercent - a.weightPercent);

/** Holdings of a saved strategy — the API stores weights as decimal fractions (0.25). */
export const holdingsFromStrategy = (assetAllocations: HoldingSource[]): ModelStrategyHolding[] =>
    toHoldings(assetAllocations, (weight) => (parseFloat(weight) || 0) * 100);

/** Holdings of a strategy drafted inside the model form — the form keeps weights in percent (25). */
export const holdingsFromDraft = (assetAllocations: HoldingSource[]): ModelStrategyHolding[] =>
    toHoldings(assetAllocations, (weight) => parseFloat(weight) || 0);

interface ModelStrategyHoldingsProps {
    holdings: ModelStrategyHolding[];
    getAssetInfo: (securityId: string) => AssetInfo | undefined;
}

const GRID_COLUMNS = 'grid-cols-[72px_minmax(120px,1fr)_72px_minmax(120px,1fr)]';

export const ModelStrategyHoldings: React.FC<ModelStrategyHoldingsProps> = ({ holdings, getAssetInfo }) => {
    if (holdings.length === 0) {
        return <p className="text-cocoa-500 text-sm">No holdings information available.</p>;
    }

    return (
        <div className="overflow-x-auto">
            <div className="min-w-[420px]">
                <div className={`grid ${GRID_COLUMNS} border-cocoa-200/50 gap-2.5 border-b pb-2`}>
                    <span className="text-cocoa-500 text-[11px] font-semibold tracking-wide uppercase">Ticker</span>
                    <span className="text-cocoa-500 text-[11px] font-semibold tracking-wide uppercase">Name</span>
                    <span className="text-cocoa-500 text-right text-[11px] font-semibold tracking-wide uppercase">
                        Weight
                    </span>
                    <span className="text-cocoa-500 text-[11px] font-semibold tracking-wide uppercase">
                        TLH replacement
                    </span>
                </div>
                {holdings.map((holding) => {
                    const assetInfo = getAssetInfo(holding.securityId);
                    return (
                        <div
                            key={holding.securityId}
                            className={`grid ${GRID_COLUMNS} border-cocoa-200/50 items-center gap-2.5 border-b py-2 last:border-b-0`}
                        >
                            <span className="text-cocoa-1000 text-sm font-semibold">
                                {assetInfo?.symbol || holding.securityId}
                            </span>
                            <span className="text-cocoa-700 truncate text-sm">
                                {assetInfo?.description || 'Unknown'}
                            </span>
                            <span className="text-cocoa-1000 text-right text-sm font-medium tabular-nums">
                                {formatPercent(holding.weightPercent)}
                            </span>
                            <div className="flex flex-wrap items-center gap-1.5">
                                {holding.replacementSecurityIds.length === 0 && (
                                    <span className="text-cocoa-400 text-xs">None</span>
                                )}
                                {holding.replacementSecurityIds.map((securityId) => (
                                    <span
                                        key={securityId}
                                        className="bg-seafoam-0 text-seafoam-600 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                                    >
                                        {getAssetInfo(securityId)?.symbol || securityId}
                                    </span>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
